import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { formatNumero } from '../lib/utils.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'codiceFiscale', label: 'Codice Fiscale', type: 'code', sortable: true },
  { key: 'dataNascita', label: 'Data Nascita', type: 'text', sortable: true, emptyText: '-' },
  { key: 'cognome', label: 'Cognome', type: 'text', sortable: true },
  { key: 'nome', label: 'Nome', type: 'text', sortable: true },
  { key: 'impresaRagioneSociale', label: 'Impresa', type: 'text', emptyText: '-' },
  { key: 'dataAssunzione', label: 'Assunzione', type: 'text', emptyText: '-' },
  { key: 'dataCessazione', label: 'Cessazione', type: 'text', emptyText: '-' },
  { key: 'sindacatoPrec', label: 'Sindacato prec.', type: 'text', sortable: true },
  { key: 'categoria', label: '', type: 'text' },
];

type Categoria = 'NUOVO' | 'EX_ALTRO' | 'CESSATO' | '';

export class TabellaConfronto extends AppComponent {
  static override componentName = 'wc-tabella-confronto';
  protected override get templateId() { return 'tabella-confronto'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  private filtroCategoria: Categoria = '';
  private filtroSindacato = '';

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriRapidi();
    this.setupRicalcola();
    this.carica();
  }

  private setupRicalcola(): void {
    this.listen(document, 'click', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="ricalcola-confronto"]') as HTMLButtonElement | null;
      if (!btn) return;
      e.preventDefault();
      void this.ricalcola(btn);
    });
  }

  private async ricalcola(btn: HTMLButtonElement): Promise<void> {
    const cassa = this.attributeString('cassa') as 'CE' | 'EC' | '';
    const periodoNuovo = this.attributeString('periodo-nuovo');
    if (!cassa || !periodoNuovo) return;

    const Weext = (globalThis as { Weext?: { confirm: (m: string) => Promise<boolean>; toast: (m: string, o?: { type?: string }) => void; alert: (m: string) => Promise<void> } }).Weext;
    const ok = Weext ? await Weext.confirm('Ricostruire il confronto dai dati attuali? La cache esistente verrà sostituita.') : true;
    if (!ok) return;

    btn.disabled = true;
    const result = await this.api.confronti.calcola(cassa, periodoNuovo, true);
    btn.disabled = false;

    if (!result.ok) {
      Weext?.alert(`Errore: ${result.error}`);
      return;
    }
    Weext?.toast('Confronto ricostruito', { type: 'success' });
    this.currentPage = 1;
    await this.carica();
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-row-click', (e: Event) => {
      const { row } = (e as CustomEvent).detail;
      window.location.href = `/lavoratori/${row.id}`;
    });
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      if (campo === 'categoria') {
        this.filtroCategoria = (wasActive ? '' : value) as Categoria;
        // NUOVO/CESSATO: il filtro sindacato precedente non e' pertinente
        if (this.filtroCategoria === 'NUOVO' || this.filtroCategoria === 'CESSATO') {
          this.filtroSindacato = '';
          this.clearFiltroGroup('sindacato');
        }
      } else if (campo === 'sindacato') {
        this.filtroSindacato = wasActive ? '' : value;
        // Selezionare un sindacato precedente implica categoria EX_ALTRO:
        // forza la selezione per evitare zero risultati.
        if (this.filtroSindacato && this.filtroCategoria === 'NUOVO') {
          this.filtroCategoria = 'EX_ALTRO';
          this.setFiltroActive('categoria', 'EX_ALTRO');
        }
      }

      this.currentPage = 1;
      this.carica();
    });
  }

  private clearFiltroGroup(campo: string): void {
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    group?.querySelectorAll('button').forEach(b => b.classList.remove('active'));
  }

  private setFiltroActive(campo: string, value: string): void {
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    group?.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === value);
    });
  }

  private async carica(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const nomeTabella = this.attributeString('nome-tabella');
    if (!nomeTabella) {
      this.dataTable.setError('Attributo nome-tabella mancante');
      return;
    }

    const result = await this.api.confronti.righe(nomeTabella, {
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
      categoria: this.filtroCategoria || undefined,
      sindacatoPrec: this.filtroSindacato || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { righe, count, totalPages, pageSize, page } = result.data;
    this.dataTable.setData(righe as unknown as Record<string, unknown>[], {
      page, totalPages, count, pageSize,
    });
    this.updateTotale(count);
    this.updateCsvLink(nomeTabella);
  }

  private updateTotale(count: number): void {
    const el = this.$('[data-totale-confronto]');
    if (el) el.textContent = formatNumero(count) + ' risultati';
  }

  private updateCsvLink(nomeTabella: string): void {
    const link = document.querySelector<HTMLAnchorElement>('[data-csv-link]');
    if (!link) return;
    const params = new URLSearchParams();
    if (this.currentFilter) params.set('q', this.currentFilter);
    if (this.filtroCategoria) params.set('categoria', this.filtroCategoria);
    if (this.filtroSindacato) params.set('sindacatoPrec', this.filtroSindacato);
    const qs = params.toString();
    link.href = `/confronti/${nomeTabella}/csv${qs ? `?${qs}` : ''}`;
  }
}
